import React, { useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { OModalMediaProps, OModalMediaType } from './OModalMedia.types';
import { Wrapper, HeaderBar, MediaContainer, VideoContainer, SafeZone } from './OModalMedia.styles';
import { ASubheader } from '../../atoms/ASubheader';
import { ASubheaderSize } from '../../atoms/ASubheader/ASubheader.types';
import { MButtonIcon } from '../../molecules/MButtonIcon';
import { AIframe } from '../../atoms/AIframe';
import { AVideo } from '../../atoms/AVideo';
import { AVideoType } from '../../atoms/AVideo/AVideo.types';
import { AScrimGradient, AScrimGradientDirection } from '../../atoms/AScrimGradient';
import { DEFAULT_CLOSE_DIALOG_TEXT } from '../../utils/i18nConstants';
import { usePortal } from '../../hooks';

const COMPONENT_NAME = 'o-web-ui-kit-OModalMedia';
const TEST_ID = 'web-ui-kit-OModalMedia';

const FALLBACK_ARIA_LABEL = 'Media modal';

export function OModalMedia({
  type = OModalMediaType.iFrame,
  subheader,
  aSubheader,
  aIframe,
  aVideo,
  isOpen = false,
  onClose,
  triggerRef,
  i18nProperties,
  portal = false,
  playbackMode: _playbackMode = 'modal',
  ...rest
}: OModalMediaProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const subheaderId = useId();

  const hasSubheader = Boolean(subheader && aSubheader?.text);
  const isVideo = type === OModalMediaType.Video;
  // Video scrim when subheader shown; aScrimGradient only tunes direction.
  const showVideoScrim = isVideo && hasSubheader;

  const { PortalWrapper } = usePortal({ enabled: portal });

  // Defer media mount until first open — keeps Brightcove/iframe inert when
  // OModalMedia is always-mounted but not yet visible (e.g. carousel slides).
  const [hasOpened, setHasOpened] = useState(isOpen);
  useEffect(() => {
    if (isOpen && !hasOpened) setHasOpened(true);
  }, [isOpen, hasOpened]);

  // Sync isOpen with showModal()/close() for top-layer, backdrop, Esc, focus trap.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Route close to onClose and refocus trigger. Esc fires close after cancel, so close alone covers all dismissals.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = () => {
      onClose?.();
      triggerRef?.current?.focus();
    };
    const handleMouseDown = (event: MouseEvent) => event.currentTarget === event.target && handleClose();

    dialog.addEventListener('close', handleClose);
    dialog.addEventListener('mousedown', handleMouseDown);
    return () => {
      dialog.removeEventListener('close', handleClose);
      dialog.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onClose, triggerRef]);

  const renderSubheader = () =>
    hasSubheader && aSubheader ? (
      <ASubheader
        id={subheaderId}
        text={aSubheader.text}
        size={ASubheaderSize.L}
        element={aSubheader.element}
        className={clsx('o-modal-media__subheader', aSubheader.className)}
        data-testid="o-modal-media-subheader"
      />
    ) : null;

  const closeButtonAriaLabel = i18nProperties?.closeDialog ?? DEFAULT_CLOSE_DIALOG_TEXT;

  const renderCloseButton = () => (
    <MButtonIcon
      aria-label={closeButtonAriaLabel}
      aIcon={{ icon: 'icon-clear' }}
      className="standard"
      data-testid="o-modal-media-close-button"
      onClick={() => dialogRef.current?.close()}
    />
  );

  return (
    <PortalWrapper>
      <Wrapper
        ref={dialogRef}
        $variant={type}
        aria-modal="true"
        {...(hasSubheader ? { 'aria-labelledby': subheaderId } : { 'aria-label': FALLBACK_ARIA_LABEL })}
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        {...rest}
      >
        {isVideo ? (
          <VideoContainer>
            <SafeZone>
              {showVideoScrim && (
                <AScrimGradient className="o-modal-media-scrim" direction={AScrimGradientDirection.TopToBottom} />
              )}
              {renderSubheader()}
              {renderCloseButton()}
            </SafeZone>
            <MediaContainer $variant={type} data-testid="o-modal-media-content">
              {aVideo && hasOpened && (
                <AVideo
                  className="o-modal-media__video"
                  {...aVideo}
                  isPlaying={aVideo.isPlaying ?? isOpen}
                  {...(aVideo.videoType === AVideoType.Brightcove ? { titleBar: !hasSubheader } : {})}
                />
              )}
            </MediaContainer>
          </VideoContainer>
        ) : (
          <>
            <HeaderBar>
              {renderSubheader()}
              {renderCloseButton()}
            </HeaderBar>
            <MediaContainer $variant={type} data-testid="o-modal-media-content">
              {aIframe && hasOpened && (
                <AIframe className="o-modal-media__iframe" {...aIframe} title={aSubheader?.text ?? aIframe.title} />
              )}
            </MediaContainer>
          </>
        )}
      </Wrapper>
    </PortalWrapper>
  );
}
